import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { ChatHistoryUsageService } from 'src/app/services/chat-history-usage.service';
import {
  ChatHistoryGetResponse,
  ChatHistoryUsageItem,
  DailyUsagePoint,
  UsageKpiSummary,
} from './models/chat-history-usage.model';

type DatePreset = '7d' | '30d' | 'month' | 'custom';

@Component({
  selector: 'app-ai-usage-dashboard',
  templateUrl: './ai-usage-dashboard.component.html',
  styleUrls: ['./ai-usage-dashboard.component.scss'],
})
export class AiUsageDashboardComponent implements OnInit, OnDestroy {
  datePreset: DatePreset = '7d';
  fromDate: Date | null = null;
  toDate: Date | null = null;
  readonly today = new Date();

  loading = false;
  errorMessage = '';
  nextToken: string | null = null;

  records: ChatHistoryUsageItem[] = [];
  dailyUsage: DailyUsagePoint[] = [];
  maxDailyTokens = 1;
  axisMax = 1;

  kpis: UsageKpiSummary = this.emptyKpis();

  detailOpen = false;
  detailLoading = false;
  detailError = '';
  selectedConversation: ChatHistoryUsageItem | null = null;
  conversationDetail: ChatHistoryGetResponse | null = null;

  gridApi: GridApi | null = null;
  columnDefs: ColDef[] = [
    { field: 'user_id', headerName: 'User', minWidth: 140, flex: 1 },
    { field: 'preview', headerName: 'Preview', minWidth: 180, flex: 2 },
    { field: 'message_count', headerName: 'Messages', width: 100 },
    {
      field: 'input_tokens',
      headerName: 'Input Tokens',
      width: 115,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    {
      field: 'output_tokens',
      headerName: 'Output Tokens',
      width: 120,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    {
      field: 'total_tokens',
      headerName: 'Total Tokens',
      width: 115,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    {
      field: 'last_message_at',
      headerName: 'Last Activity',
      minWidth: 160,
      flex: 1,
      valueFormatter: (p) => this.formatDateTime(p.value),
    },
    {
      field: 'total_llm_time',
      headerName: 'LLM (s)',
      width: 90,
      valueFormatter: (p) => this.formatSeconds(p.value),
    },
    {
      field: 'total_db_time',
      headerName: 'DB (s)',
      width: 90,
      valueFormatter: (p) => this.formatSeconds(p.value),
    },
  ];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
  };

  messageColumnDefs: ColDef[] = [
    { field: 'role', headerName: 'Role', width: 90 },
    {
      field: 'timestamp',
      headerName: 'Time',
      minWidth: 140,
      flex: 1,
      valueFormatter: (p) => this.formatDateTime(p.value),
    },
    {
      field: 'input_tokens',
      headerName: 'Input Tokens',
      width: 105,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    {
      field: 'output_tokens',
      headerName: 'Output Tokens',
      width: 110,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    {
      field: 'total_tokens',
      headerName: 'Total Tokens',
      width: 105,
      type: 'numericColumn',
      valueFormatter: (p) => this.formatNumber(p.value),
    },
    { field: 'text', headerName: 'Message', minWidth: 200, flex: 2 },
  ];

  private subscriptions: Subscription[] = [];

  constructor(private readonly usageService: ChatHistoryUsageService) {}

  ngOnInit(): void {
    this.applyPreset('7d');
    this.loadUsage();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.gridApi.sizeColumnsToFit();
  }

  onRowClicked(event: RowClickedEvent): void {
    const row = event.data as ChatHistoryUsageItem;
    if (row?.conversation_id) {
      this.openConversationDetail(row);
    }
  }

  applyPreset(preset: DatePreset): void {
    this.datePreset = preset;
    const today = new Date();

    if (preset === 'custom') {
      return;
    }

    if (preset === 'month') {
      this.fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      this.toDate = today;
      return;
    }

    const days = preset === '7d' ? 7 : 30;
    const start = new Date(today);
    start.setDate(today.getDate() - (days - 1));
    this.fromDate = start;
    this.toDate = today;
  }

  onPresetChange(preset: DatePreset): void {
    this.applyPreset(preset);
    if (preset !== 'custom') {
      this.loadUsage();
    }
  }

  applyFilters(): void {
    this.loadUsage();
  }

  /** Exports the currently loaded conversations to a formatted .xlsx file. */
  exportToExcel(): void {
    if (!this.records.length) {
      return;
    }

    const rows = this.records.map((r) => ({
      User: r.user_id ?? '',
      Preview: r.preview ?? '',
      Messages: r.message_count ?? 0,
      'Input Tokens': r.input_tokens ?? 0,
      'Output Tokens': r.output_tokens ?? 0,
      'Total Tokens': r.total_tokens ?? 0,
      'Last Activity': this.formatDateTime(r.last_message_at),
      'LLM (s)': r.total_llm_time ?? '',
      'DB (s)': r.total_db_time ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 20 },
      { wch: 40 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
    ];

    const workbook: XLSX.WorkBook = {
      Sheets: { 'AI Usage': worksheet },
      SheetNames: ['AI Usage'],
    };
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveFile(
      buffer,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      `ai-usage-${this.fileStamp()}.xlsx`
    );
  }

  closeDetail(): void {
    this.detailOpen = false;
    this.selectedConversation = null;
    this.conversationDetail = null;
    this.detailError = '';
  }

  private saveFile(buffer: ArrayBuffer, type: string, fileName: string): void {
    const blob = new Blob([buffer], { type });
    FileSaver.saveAs(blob, fileName);
  }

  private fileStamp(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(
      d.getHours()
    )}${pad(d.getMinutes())}`;
  }

  /**
   * Loads every page for the current selection by following `next_token` until
   * exhausted, so the KPIs, chart and grid reflect the full data set in one go
   * (no manual "Load more" required).
   */
  private loadUsage(): void {
    this.loading = true;
    this.records = [];
    this.nextToken = null;
    this.errorMessage = '';

    const baseBody: Record<string, unknown> = {
      page_size: 100,
    };
    if (this.fromDate) {
      baseBody['from_date'] = this.startOfDayIso(this.fromDate);
    }
    if (this.toDate) {
      baseBody['to_date'] = this.endOfDayIso(this.toDate);
    }

    const accumulated: ChatHistoryUsageItem[] = [];

    const fetchPage = (token: string | null): void => {
      const body = { ...baseBody };
      if (token) {
        body['next_token'] = token;
      }

      const sub = this.usageService.listUsage(body).subscribe({
        next: (res) => {
          accumulated.push(...(res?.records ?? []));
          const next = res?.next_token ?? null;
          if (next) {
            fetchPage(next);
            return;
          }
          this.records = accumulated;
          this.nextToken = null;
          this.recomputeSummaries();
          this.loading = false;
          setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Failed to load AI usage data.';
          this.records = accumulated;
          this.recomputeSummaries();
          this.loading = false;
        },
      });

      this.subscriptions.push(sub);
    };

    fetchPage(null);
  }

  private openConversationDetail(row: ChatHistoryUsageItem): void {
    this.detailOpen = true;
    this.selectedConversation = row;
    this.conversationDetail = null;
    this.detailLoading = true;
    this.detailError = '';

    const sub = this.usageService
      .getConversation({ conversation_id: row.conversation_id, limit: 100 })
      .subscribe({
        next: (res) => {
          this.conversationDetail = {
            ...res,
            messages: (res.messages ?? []).filter((m) => m.role !== 'user'),
          };
          this.detailLoading = false;
        },
        error: (err) => {
          this.detailError =
            err?.error?.message || err?.message || 'Failed to load conversation.';
          this.detailLoading = false;
        },
      });

    this.subscriptions.push(sub);
  }

  private recomputeSummaries(): void {
    this.kpis = this.records.reduce(
      (acc, row) => {
        acc.conversations += 1;
        acc.messages += row.message_count ?? 0;
        acc.inputTokens += row.input_tokens ?? 0;
        acc.outputTokens += row.output_tokens ?? 0;
        acc.totalTokens += row.total_tokens ?? 0;
        acc.avgLlmTime += row.total_llm_time ?? 0;
        acc.avgDbTime += row.total_db_time ?? 0;
        return acc;
      },
      this.emptyKpis()
    );

    const byDate = new Map<string, DailyUsagePoint>();

    for (const row of this.records) {
      const key = this.dateKey(row.last_message_at || row.created_at);
      if (!key) {
        continue;
      }
      const existing = byDate.get(key) ?? {
        date: key,
        label: this.formatChartLabel(key),
        tokens: 0,
        conversations: 0,
      };
      existing.tokens += row.total_tokens ?? 0;
      existing.conversations += 1;
      byDate.set(key, existing);
    }

    this.dailyUsage = Array.from(byDate.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    this.maxDailyTokens = Math.max(
      1,
      ...this.dailyUsage.map((d) => d.tokens)
    );
    this.axisMax = this.niceCeil(this.maxDailyTokens);
  }

  /** Rounds a value up to a clean axis maximum (1, 2, 2.5, 5, 10 × 10ⁿ). */
  private niceCeil(value: number): number {
    if (value <= 0) {
      return 1;
    }
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    const fraction = value / pow;
    let nice: number;
    if (fraction <= 1) {
      nice = 1;
    } else if (fraction <= 2) {
      nice = 2;
    } else if (fraction <= 2.5) {
      nice = 2.5;
    } else if (fraction <= 5) {
      nice = 5;
    } else {
      nice = 10;
    }
    return nice * pow;
  }

  /** Y-axis ticks from the top (axisMax) down to zero, evenly spaced. */
  yAxisTicks(): { value: number; label: string }[] {
    const steps = 4;
    const ticks: { value: number; label: string }[] = [];
    for (let i = steps; i >= 0; i--) {
      const value = (this.axisMax / steps) * i;
      ticks.push({ value, label: this.formatTokensShort(value) });
    }
    return ticks;
  }

  /** Compact token label for the axis (e.g. 1.2k, 3.4M). */
  formatTokensShort(value: number): string {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
    }
    return Math.round(value).toString();
  }

  /** Multi-line tooltip shown when hovering a chart bar. */
  barTooltip(point: DailyUsagePoint): string {
    const lines = [
      point.label,
      `Tokens: ${this.formatNumber(point.tokens)}`,
      `Conversations: ${this.formatNumber(point.conversations)}`,
    ];
    return lines.join('\n');
  }

  private emptyKpis(): UsageKpiSummary {
    return {
      conversations: 0,
      messages: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      avgLlmTime: 0,
      avgDbTime: 0,
    };
  }

  /** ISO string at the start (00:00:00) of the picked calendar day. */
  private startOfDayIso(date: Date): string {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0
    ).toISOString();
  }

  /** ISO string at the end (23:59:59.999) of the picked calendar day. */
  private endOfDayIso(date: Date): string {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    ).toISOString();
  }

  private dateKey(value?: string): string {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value.slice(0, 10);
    }
    return parsed.toISOString().slice(0, 10);
  }

  formatNumber(value?: number | null): string {
    if (value == null) {
      return '—';
    }
    return value.toLocaleString();
  }

  formatSeconds(value?: number | null): string {
    if (value == null) {
      return '—';
    }
    return value.toFixed(2);
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  }

  formatChartLabel(dateKey: string): string {
    const parsed = new Date(dateKey + 'T00:00:00');
    if (Number.isNaN(parsed.getTime())) {
      return dateKey;
    }
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  barHeight(tokens: number): string {
    if (!tokens || this.axisMax <= 0) {
      return '0%';
    }
    const pct = Math.max(1, Math.round((tokens / this.axisMax) * 100));
    return `${pct}%`;
  }
}

